import React, { useState, useEffect } from "react";
import { FiX } from "react-icons/fi";
import Scrollbars from "react-custom-scrollbars-2";

import DrawerButton from "components/form/DrawerButton";
import Error from "components/form/Error";
import InputArea from "components/form/InputArea";
import LabelArea from "components/form/LabelArea";
import Title from "components/form/Title";
import useVehicleCategorySubmit from "hooks/useVehicleCategorySubmit";
import SelectBasicType from "components/form/SelectBasicType";
import Uploader from "components/image-uploader/Uploader";

const VehicleModelDrawer = ({ id, isViewMode }) => {
  const [carUrl, setCarUrl] = useState("");
  const [primeCarUrl, setPrimeCarUrl] = useState("");

  const {
    trigger,
    register,
    handleSubmit,
    onSubmit,
    errors,
    existingImage,
    existingPrimeImage,
    isPrime,
    setIsPrime,
    loading,
  } = useVehicleCategorySubmit(
    id,
    carUrl,
    setCarUrl,
    primeCarUrl,
    setPrimeCarUrl
  );

  const handleCancelImage = (value) => {
    if (value === "type") setCarUrl("");
    else if (value === "primetype") setPrimeCarUrl("");
  };

  useEffect(() => {
    setCarUrl(id && existingImage ? existingImage : "");
  }, [id, existingImage]);

  useEffect(() => {
    setPrimeCarUrl(id && existingPrimeImage ? existingPrimeImage : "");
  }, [id, existingPrimeImage]);

  const amenitiesValue = ["AC", "Charger", "Music", "Carrier", "TV","First Aid Kit"];

  return (
    <>
      <div className="w-full relative p-6 border-b border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
        <Title
          title={
            isViewMode
              ? "View Vehicle Category"
              : id
              ? "Edit Vehicle Category"
              : "Add Vehicle Category"
          }
        />
      </div>

      <Scrollbars className="w-full md:w-7/12 lg:w-8/12 xl:w-8/12 relative dark:bg-gray-700 dark:text-gray-200">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="px-5 pt-5 pb-20 flex-grow w-full max-h-full">
            <div className="grid grid-cols-12 gap-5 mb-6 items-center">
              {/* Vehicle Category */}
              <div className="col-span-6">
                <LabelArea required label="Vehicle Category" />
                <SelectBasicType
                  register={register}
                  name="vehicle_type"
                  label="Vehicle Category"
                  type="text"
                  placeholder="Vehicle Category"
                  readOnly={isViewMode}
                />
                <Error errorName={errors.vehicle_type} />
              </div>

              {/* Similar Vehicle */}
              {/* <div className="col-span-6">
                <LabelArea required label="Similar Vehicle" />
                <InputArea
                  trigger={trigger}
                  register={register}
                  name="similar_vehicle"
                  label="Similar"
                  type="text"
                  placeholder="Similar Vehicle"
                  maxLength={120}
                  readOnly={isViewMode}
                />
                <Error errorName={errors.similar_vehicle} />
              </div> */}

              {/* Seats */}
              <div className="col-span-6">
                <LabelArea required label="Seats" />
                <InputArea
                  trigger={trigger}
                  register={register}
                  name="seats"
                  label="Seats"
                  type="number"
                  maxLength={2}
                  placeholder="Seats"
                  pattern={/^[0-9]*$/}
                  readOnly={isViewMode}
                />
                <Error errorName={errors.seats} />
              </div>

              {/* Large Bags */}
              <div className="col-span-6">
                <LabelArea required label="Large Bags" />
                <InputArea
                  trigger={trigger}
                  register={register}
                  name="no_of_bags"
                  label="Large Bags"
                  type="number"
                  maxLength={1}
                  placeholder="Large Bags"
                  readOnly={isViewMode}
                  allowZero={true}
                />
                <Error errorName={errors.no_of_bags} />
              </div>

              {/* Small Bags */}
              <div className="col-span-6">
                <LabelArea required label="Small Bags" />
                <InputArea
                  trigger={trigger}
                  register={register}
                  name="no_of_small_bags"
                  label="Small Bags"
                  type="number"
                  maxLength={1}
                  placeholder="Small Bags"
                  readOnly={isViewMode}
                  allowZero={true}
                />
                <Error errorName={errors.no_of_small_bags} />
              </div>

              {/* Vehicle Image */}
              <div className="col-span-12">
                <LabelArea required label="Vehicle Image" />
                {carUrl ? (
                  <div className="flex items-center gap-2 relative">
                    <img
                      src={`${process.env.REACT_APP_API_IMAGE_URL}${carUrl}`}
                      alt="caricon"
                      style={{ objectFit: "contain", width: 100, height: 100 }}
                    />
                    {!isViewMode && (
                      <FiX
                        size={20}
                        className="cursor-pointer"
                        onClick={() => handleCancelImage("type")}
                      />
                    )}
                  </div>
                ) : (
                  <Uploader
                    is_multiple={true}
                    folder="car"
                    name_merge="Type"
                    imageUrl={carUrl}
                    acceptType="image/png"
                    setImageUrl={setCarUrl}
                    no_of_image={1}
                  />
                )}
              </div>

              {/* Amenities */}
              <div className="col-span-6">
                <LabelArea label="Amenities" />
              </div>
              <div className="col-span-8 flex flex-wrap gap-5 items-center">
                {amenitiesValue.map((item, index) => (
                  <label
                    key={item}
                    className="flex items-center justify-center m-0"
                  >
                    <input
                      disabled={isViewMode}
                      type="checkbox"
                      name="amenities"
                      value={index + 1}
                      className="checkbox-custom"
                      {...register("amenities")}
                    />
                    <span className="ml-3 text-sm">{item}</span>
                  </label>
                ))}
                <Error errorName={errors.amenities} />
              </div>

              {/* Prime Available */}
              <div className="col-span-6">
                <LabelArea label="Prime Available" />
              </div>
              <div className="col-span-8">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={isPrime}
                    className="checkbox-custom"
                    {...register("is_prime")}
                    onChange={(e) => setIsPrime(e.target.checked)}
                    disabled={isViewMode}
                  />
                  <span className="text-sm">Is Prime</span>
                </label>
              </div>

              {/* Prime Image */}
              <div className="col-span-6">
                <LabelArea required label="Prime Image" />
                {primeCarUrl ? (
                  <div className="flex items-center gap-2 relative">
                    <img
                      src={`${process.env.REACT_APP_API_IMAGE_URL}${primeCarUrl}`}
                      alt="primecaricon"
                      style={{ objectFit: "contain", width: 100, height: 100 }}
                    />
                    {!isViewMode && (
                      <FiX
                        size={20}
                        className="cursor-pointer"
                        onClick={() => handleCancelImage("primetype")}
                      />
                    )}
                  </div>
                ) : (
                  <Uploader
                    is_multiple={true}
                    folder="car"
                    name_merge="PrimeType"
                    imageUrl={primeCarUrl}
                    acceptType="image/png"
                    setImageUrl={setPrimeCarUrl}
                    no_of_image={1}
                  />
                )}
              </div>
            </div>
          </div>

          {!isViewMode && (
            <DrawerButton
              isSubmitting={loading}
              id={id}
              title={id ? "Update" : "Submit"}
            />
          )}
        </form>
      </Scrollbars>
    </>
  );
};

export default VehicleModelDrawer;
